import type { PortalStatement } from '@erp/shared';
import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/shadcn/index.js';
import { Button, Card, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatMoney } from '../../lib/money.js';

import { downloadPortalPdf } from './download.js';
import { PortalInvoicesPanel } from './PortalInvoicesPage.js';
import { PortalPaymentsPanel } from './PortalPaymentsPage.js';

const TAB_IDS = ['statement', 'invoices', 'payments'] as const;
type TabId = (typeof TAB_IDS)[number];

/**
 * The customer's money, in ONE place: the statement (per-currency summary +
 * aging), the invoices behind those numbers and the payments against them —
 * as tabs of a single page. They used to be three separate nav entries; the
 * invoice and payment lists moved in here and their old routes redirect.
 *
 * The tab rides `?tab=` so a link can open straight to invoices or payments
 * (the redirects from the old routes do exactly that) and back/forward keeps
 * working. Radix unmounts inactive tab content, so each tab fetches only when
 * it is first opened.
 */
export function PortalStatementPage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const tab: TabId = (TAB_IDS as readonly string[]).includes(rawTab ?? '') ? (rawTab as TabId) : 'statement';
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState<PortalStatement | null>(null);

  useEffect(() => {
    const p: Record<string, string> = {};
    if (from) p.from = from;
    if (to) p.to = to;
    setData(null);
    void customerApi()
      .get<PortalStatement>('/statement', { params: p })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [from, to]);

  const money = (v: string, c: string): string => formatMoney(v, { locale: i18n.language, currency: c });

  const pdfQuery = (): string => {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const s = q.toString();
    return s ? `?${s}` : '';
  };

  return (
    <div className="flex flex-col gap-4 pb-16 lg:pb-0">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('statement.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('statement.subtitle')}</p>
        </div>
        <Button variant="secondary" onClick={() => void downloadPortalPdf(`/statement/pdf${pdfQuery()}`, `statement-${data?.asOf ?? 'now'}.pdf`, t('loadError'))}>
          <Download className="me-1.5 h-4 w-4" aria-hidden />
          {t('statement.download')}
        </Button>
      </header>

      <Tabs value={tab} onValueChange={(v) => setParams(v === 'statement' ? {} : { tab: v }, { replace: true })}>
        <TabsList className="h-10">
          <TabsTrigger value="statement">{t('nav.statement')}</TabsTrigger>
          <TabsTrigger value="invoices">{t('nav.invoices')}</TabsTrigger>
          <TabsTrigger value="payments">{t('nav.payments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="statement" className="mt-4 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1.5 text-sm text-fg-muted">
              {t('statement.from')}
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-md border border-border bg-bg px-2 text-sm text-fg" />
            </label>
            <label className="flex items-center gap-1.5 text-sm text-fg-muted">
              {t('statement.to')}
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-md border border-border bg-bg px-2 text-sm text-fg" />
            </label>
          </div>

          {!data ? (
            <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>
          ) : (
            <>
              {/* Per-currency summary */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.summary.length === 0 ? (
                  <Card className="p-4 text-sm text-fg-muted">{t('statement.empty')}</Card>
                ) : (
                  data.summary.map((s) => (
                    <Card key={s.currencyCode} className="p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{s.currencyCode}</div>
                      <dl className="mt-2 space-y-1 text-sm">
                        <div className="flex justify-between"><dt className="text-fg-muted">{t('statement.summary.invoiced')}</dt><dd className="font-mono tabular-nums">{money(s.invoiced, s.currencyCode)}</dd></div>
                        <div className="flex justify-between"><dt className="text-fg-muted">{t('statement.summary.paid')}</dt><dd className="font-mono tabular-nums">{money(s.paid, s.currencyCode)}</dd></div>
                        <div className="flex justify-between border-t border-border pt-1 font-semibold"><dt>{t('statement.summary.balance')}</dt><dd className="font-mono tabular-nums">{money(s.balance, s.currencyCode)}</dd></div>
                      </dl>
                    </Card>
                  ))
                )}
              </div>

              {/* Aging */}
              {data.aging.length > 0 && (
                <section>
                  <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('statement.aging.title')}</h2>
                  <Card className="overflow-x-auto p-0">
                    <table className="w-full text-sm">
                      <thead className="bg-bg-subtle text-fg-muted">
                        <tr>
                          <th className="px-3 py-2 text-start font-semibold">{t('columns.amount')}</th>
                          <th className="px-3 py-2 text-end font-semibold">{t('statement.aging.current')}</th>
                          <th className="px-3 py-2 text-end font-semibold">{t('statement.aging.d1_30')}</th>
                          <th className="px-3 py-2 text-end font-semibold">{t('statement.aging.d31_60')}</th>
                          <th className="px-3 py-2 text-end font-semibold">{t('statement.aging.d61_90')}</th>
                          <th className="px-3 py-2 text-end font-semibold">{t('statement.aging.d90')}</th>
                          <th className="px-3 py-2 text-end font-semibold">{t('statement.aging.total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.aging.map((a) => (
                          <tr key={a.currencyCode} className="border-t border-border font-mono tabular-nums">
                            <td className="px-3 py-2 font-sans">{a.currencyCode}</td>
                            <td className="px-3 py-2 text-end">{money(a.current, a.currencyCode)}</td>
                            <td className="px-3 py-2 text-end">{money(a.days_1_30, a.currencyCode)}</td>
                            <td className="px-3 py-2 text-end">{money(a.days_31_60, a.currencyCode)}</td>
                            <td className="px-3 py-2 text-end">{money(a.days_61_90, a.currencyCode)}</td>
                            <td className="px-3 py-2 text-end">{money(a.days_90_plus, a.currencyCode)}</td>
                            <td className="px-3 py-2 text-end font-semibold">{money(a.total, a.currencyCode)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </section>
              )}
            </>
          )}
        </TabsContent>

        {/* The full invoice / payment tables (status, balance, PDF, detail
            links) — the statement tab no longer duplicates them as bare lists. */}
        <TabsContent value="invoices" className="mt-4">
          <PortalInvoicesPanel />
        </TabsContent>
        <TabsContent value="payments" className="mt-4">
          <PortalPaymentsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
